// frontend/src/pages/Reinsurance/components/ContractManagement/ContractDetail/index.js

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faEdit, faExclamationCircle, faSync,
  faFileContract, faBuilding, faMoneyBillWave, faCalendarAlt,
  faShieldAlt, faChartLine, faUsers, faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { reinsuranceService } from '../../../../../services/reinsurance';
import styles from './ContractDetail.module.css';

const ContractDetail = ({ contractId, onBack, onEdit }) => {
  const { t } = useLanguage();
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Fetch contract details
  const fetchContractDetails = useCallback(async () => {
    if (!contractId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await reinsuranceService.getContract(contractId);
      
      if (response && response.success) {
        setContract(response.data);
      } else {
        throw new Error(response?.message || 'Failed to load contract details');
      }
    } catch (err) {
      console.error('Error fetching contract details:', err);
      setError(err.message || 'Failed to load contract details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [contractId]);
  
  // Fetch data on component mount
  useEffect(() => {
    fetchContractDetails();
  }, [fetchContractDetails]);
  
  // Format date using the service helper
  const formatDate = (dateString) => {
    return reinsuranceService.formatDate(dateString);
  };
  
  // Format currency using the service helper
  const formatCurrency = (amount, currency = 'USD') => {
    return reinsuranceService.formatCurrency(amount, currency);
  };
  
  // Format percentage using the service helper
  const formatPercentage = (value) => {
    return reinsuranceService.formatPercentage(value);
  };
  
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t('loading_contract_details')}</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={fetchContractDetails}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }
  
  if (!contract) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{t('contract_not_found')}</p>
        <button className={styles.backButton} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_contracts')}</span>
        </button>
      </div>
    );
  }
  
  return (
    <div className={styles.contractDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_contracts')}</span>
        </button>
        <h1 className={styles.title}>
          {contract.contract_number} - {contract.rnr}
        </h1>
        <button className={styles.editButton} onClick={onEdit}>
          <FontAwesomeIcon icon={faEdit} />
          <span>{t('edit_contract')}</span>
        </button>
      </div>
      
      <div className={styles.statusBar}>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('status')}:</span>
          <span className={`${styles.statusBadge} ${styles[contract.performance?.current_status || 'draft']}`}>
            {contract.performance?.current_status || 'draft'}
          </span>
        </div>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('cedent')}:</span>
          <span className={styles.statusValue}>{contract.cedent?.cedent_name || '-'}</span>
        </div>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('type')}:</span>
          <span className={styles.statusValue}>{contract.contract_type || '-'}</span>
        </div>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>{t('structure')}:</span>
          <span className={styles.statusValue}>{contract.contract_structure || '-'}</span>
        </div>
      </div>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FontAwesomeIcon icon={faFileContract} />
          <span>{t('overview')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'cedent' ? styles.active : ''}`}
          onClick={() => setActiveTab('cedent')}
        >
          <FontAwesomeIcon icon={faBuilding} />
          <span>{t('cedent')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'terms' ? styles.active : ''}`}
          onClick={() => setActiveTab('terms')}
        >
          <FontAwesomeIcon icon={faCalendarAlt} />
          <span>{t('terms')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'risk' ? styles.active : ''}`}
          onClick={() => setActiveTab('risk')}
        >
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>{t('risk')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'capital' ? styles.active : ''}`}
          onClick={() => setActiveTab('capital')}
        >
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>{t('capital')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'performance' ? styles.active : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          <FontAwesomeIcon icon={faChartLine} />
          <span>{t('performance')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'claims' ? styles.active : ''}`}
          onClick={() => setActiveTab('claims')}
        >
          <FontAwesomeIcon icon={faClipboardList} />
          <span>{t('claims')}</span>
        </button>
      </div>
      
      <div className={styles.tabContent}>
        {activeTab === 'overview' && (
          <div className={styles.overviewTab}>
            <div className={styles.overviewGrid}>
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faFileContract} />
                  <span>{t('contract_details')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('contract_number')}</span>
                    <span className={styles.detailValue}>{contract.contract_number}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('rnr')}</span>
                    <span className={styles.detailValue}>{contract.rnr}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('contract_type')}</span>
                    <span className={styles.detailValue}>{contract.contract_type}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('contract_structure')}</span>
                    <span className={styles.detailValue}>{contract.contract_structure}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('status')}</span>
                    <span className={`${styles.statusBadge} ${styles[contract.performance?.current_status || 'draft']}`}>
                      {contract.performance?.current_status || 'draft'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>{t('key_dates')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('effective_date')}</span>
                    <span className={styles.detailValue}>{formatDate(contract.terms?.effective_date)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('expiration_date')}</span>
                    <span className={styles.detailValue}>{formatDate(contract.terms?.expiration_date)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('created_at')}</span>
                    <span className={styles.detailValue}>{formatDate(contract.metadata?.created_at)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('updated_at')}</span>
                    <span className={styles.detailValue}>{formatDate(contract.metadata?.updated_at)}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faBuilding} />
                  <span>{t('cedent')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('cedent_name')}</span>
                    <span className={styles.detailValue}>{contract.cedent?.cedent_name || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('cedent_domicile')}</span>
                    <span className={styles.detailValue}>{contract.cedent?.cedent_domicile || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('am_best_rating')}</span>
                    <span className={styles.detailValue}>{contract.cedent?.cedent_rating?.am_best || '-'}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.overviewCard}>
                <h3 className={styles.cardTitle}>
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  <span>{t('financial_summary')}</span>
                </h3>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('limit')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.terms?.limit, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('premium')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.terms?.premium?.base_amount, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('expected_loss')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.risk?.expected_loss, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('loss_ratio')}</span>
                    <span className={styles.detailValue}>{formatPercentage(contract.risk?.risk_metrics?.loss_ratio)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'cedent' && (
          <div className={styles.cedentTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('cedent_information')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('cedent_name')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_name || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('cedent_id')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_id || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('cedent_domicile')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_domicile || '-'}</span>
                </div>
              </div>
            </div>
            
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('cedent_ratings')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('am_best')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_rating?.am_best || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('sp')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_rating?.sp || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('moodys')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_rating?.moodys || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('fitch')}</span>
                  <span className={styles.detailValue}>{contract.cedent?.cedent_rating?.fitch || '-'}</span>
                </div>
              </div>
            </div>
            
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('broker_information')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('broker_name')}</span>
                  <span className={styles.detailValue}>{contract.broker?.broker_name || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('broker_contact_name')}</span>
                  <span className={styles.detailValue}>{contract.broker?.broker_contact?.name || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('broker_contact_email')}</span>
                  <span className={styles.detailValue}>{contract.broker?.broker_contact?.email || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('broker_commission')}</span>
                  <span className={styles.detailValue}>
                    {contract.broker?.broker_commission?.percentage 
                      ? `${contract.broker.broker_commission.percentage}% (${formatCurrency(contract.broker.broker_commission.amount, contract.broker.broker_commission.currency)})`
                      : '-'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'terms' && (
          <div className={styles.termsTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('contract_terms')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('effective_date')}</span>
                  <span className={styles.detailValue}>{formatDate(contract.terms?.effective_date)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('expiration_date')}</span>
                  <span className={styles.detailValue}>{formatDate(contract.terms?.expiration_date)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('currency')}</span>
                  <span className={styles.detailValue}>{contract.terms?.currency || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('limit')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.terms?.limit, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('attachment_point')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.terms?.attachment_point, contract.terms?.currency)}</span>
                </div>
              </div>
            </div>
            
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('premium_details')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('base_premium')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.terms?.premium?.base_amount, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('adjustable')}</span>
                  <span className={styles.detailValue}>{contract.terms?.premium?.adjustable ? t('yes') : t('no')}</span>
                </div>
                {contract.terms?.premium?.adjustable && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('adjustment_basis')}</span>
                    <span className={styles.detailValue}>{contract.terms?.premium?.adjustment_basis || '-'}</span>
                  </div>
                )}
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('minimum_premium')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.terms?.premium?.minimum_premium, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('deposit_premium')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.terms?.premium?.deposit_premium, contract.terms?.currency)}</span>
                </div>
              </div>
            </div>
            
            {contract.terms?.premium_payment_schedule && contract.terms.premium_payment_schedule.length > 0 && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('payment_schedule')}</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('due_date')}</th>
                        <th>{t('amount')}</th>
                        <th>{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.terms.premium_payment_schedule.map((payment, index) => (
                        <tr key={index}>
                          <td>{formatDate(payment.due_date)}</td>
                          <td>{formatCurrency(payment.amount, payment.currency || contract.terms.currency)}</td>
                          <td>
                            <span className={`${styles.statusBadge} ${styles[payment.status]}`}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {contract.terms?.reinstatements && contract.terms.reinstatements.length > 0 && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('reinstatements')}</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('number')}</th>
                        <th>{t('premium_percentage')}</th>
                        <th>{t('coverage_percentage')}</th>
                        <th>{t('used')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.terms.reinstatements.map((reinstatement, index) => (
                        <tr key={index}>
                          <td>{reinstatement.number}</td>
                          <td>{formatPercentage(reinstatement.premium_percentage)}</td>
                          <td>{formatPercentage(reinstatement.coverage_percentage)}</td>
                          <td>{reinstatement.used ? t('yes') : t('no')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'risk' && (
          <div className={styles.riskTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('risk_profile')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('expected_loss')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.risk?.expected_loss, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('probable_maximum_loss')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.risk?.probable_maximum_loss, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('loss_ratio')}</span>
                  <span className={styles.detailValue}>{formatPercentage(contract.risk?.risk_metrics?.loss_ratio)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('rate_on_line')}</span>
                  <span className={styles.detailValue}>{formatPercentage(contract.risk?.risk_metrics?.rate_on_line)}</span>
                </div>
              </div>
            </div>
            
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('covered_perils_and_territories')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('covered_perils')}</span>
                  <span className={styles.detailValue}>
                    {contract.risk?.covered_perils?.length > 0 
                      ? contract.risk.covered_perils.join(', ') 
                      : '-'
                    }
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('territories')}</span>
                  <span className={styles.detailValue}>
                    {contract.risk?.territories?.length > 0 
                      ? contract.risk.territories.join(', ') 
                      : '-'
                    }
                  </span>
                </div>
              </div>
            </div>
            
            {contract.risk?.modeling_results && contract.risk.modeling_results.length > 0 && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('modeling_results')}</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('model')}</th>
                        <th>{t('expected_loss')}</th>
                        <th>{t('standard_deviation')}</th>
                        <th>{t('tail_value_at_risk')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.risk.modeling_results.map((result, index) => (
                        <tr key={index}>
                          <td>{`${result.model_name} ${result.model_version}`}</td>
                          <td>{formatCurrency(result.expected_loss, contract.terms?.currency)}</td>
                          <td>{formatCurrency(result.standard_deviation, contract.terms?.currency)}</td>
                          <td>{formatCurrency(result.tail_value_at_risk, contract.terms?.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'capital' && (
          <div className={styles.capitalTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('capital_allocation')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('total_allocated')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.capital_allocation?.total_allocated, contract.terms?.currency)}</span>
                </div>
              </div>
            </div>
            
            {contract.capital_allocation?.allocations && contract.capital_allocation.allocations.length > 0 && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('investor_allocations')}</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('investor')}</th>
                        <th>{t('amount')}</th>
                        <th>{t('percentage')}</th>
                        <th>{t('expected_return')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.capital_allocation.allocations.map((allocation, index) => (
                        <tr key={index}>
                          <td>{allocation.investor_name}</td>
                          <td>{formatCurrency(allocation.amount, contract.terms?.currency)}</td>
                          <td>{formatPercentage(allocation.percentage)}</td>
                          <td>{formatPercentage(allocation.expected_return)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {contract.capital_allocation?.collateral_requirements && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('collateral_requirements')}</h3>
                <div className={styles.sectionContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('total_required')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.capital_allocation.collateral_requirements.total_required, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('posted')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.capital_allocation.collateral_requirements.posted, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('release_conditions')}</span>
                    <span className={styles.detailValue}>{contract.capital_allocation.collateral_requirements.release_conditions || '-'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'performance' && (
          <div className={styles.performanceTab}>
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>{t('performance_metrics')}</h3>
              <div className={styles.sectionContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('loss_ratio')}</span>
                  <span className={styles.detailValue}>{formatPercentage(contract.performance?.loss_ratio)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('incurred_losses')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.performance?.incurred_losses, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('paid_losses')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.performance?.paid_losses, contract.terms?.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('underwriting_result')}</span>
                  <span className={styles.detailValue}>{formatCurrency(contract.performance?.underwriting_result, contract.terms?.currency)}</span>
                </div>
              </div>
            </div>
            
            {contract.performance?.reserves && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('reserves')}</h3>
                <div className={styles.sectionContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('case_reserves')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.performance.reserves.case_reserves, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('ibnr_reserves')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.performance.reserves.ibnr_reserves, contract.terms?.currency)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('total_reserves')}</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.performance.reserves.total_reserves, contract.terms?.currency)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'claims' && (
          <div className={styles.claimsTab}>
            {contract.performance?.claims && contract.performance.claims.length > 0 ? (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>{t('claims')}</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('claim_number')}</th>
                        <th>{t('incurred_loss')}</th>
                        <th>{t('paid_loss')}</th>
                        <th>{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.performance.claims.map((claim, index) => (
                        <tr key={index}>
                          <td>{claim.claim_number}</td>
                          <td>{formatCurrency(claim.incurred_loss, contract.terms?.currency)}</td>
                          <td>{formatCurrency(claim.paid_loss, contract.terms?.currency)}</td>
                          <td>
                            <span className={`${styles.statusBadge} ${styles[claim.claim_status]}`}>
                              {claim.claim_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>{t('no_claims_found')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDetail;